import { useCallback } from 'react';
import { ZodError } from 'zod';
import {
  Accordion,
  Button,
  Flex,
  GridAlpha,
  createForm,
  useId,
  useToasts,
} from '@applyboard/crystal-ui';
import { ArrowLeftOutlineIcon, ArrowRightOutlineIcon } from '@applyboard/ui-icons';
import { useEventTracker, useTranslation } from '@applyboard/ui-utils';
import {
  CreateLoanApplicationV2Request,
  LoansError,
  useLoanApplicationSubmissionV2,
} from '../../../../../src/data/loans';
import { useGetSource, useGetUtmSource } from '../../../../common/hooks/useQueryParams';
import { getBase64 } from '../../../../common/utils/getBase64';
import { EnglishExamSchema, GMATScoresSchema, GREScoresSchema } from '../../formSchemas';
import { LoanData, LoansFormEvents } from '../../types';
import { StepperState } from '../LoanFormStepper';
import { GmatScoreTag, GreScoreTag } from './CompetitiveScoreTags';
import EnglishTestScores from './EnglishTestScore';
import GMATScores from './GmatScores';
import GREScores from './GreScores';
import { EnglishTestScoreTag } from './englishTestScores';
import { EducationSummaryForm } from './types';

const { Field, Form, useFieldValues, useSetFieldValues } = createForm<EducationSummaryForm>();

export const DEFAULT_EDUCATIONAL_SUMMARY_VALUES: EducationSummaryForm = {
  englishTestType: '',
  englishDateOfExam: '',
  englishTestScoreDocument: [],
  englishReadingScore: '',
  englishWritingScore: '',
  englishListeningScore: '',
  englishSpeakingScore: '',
  englishOverallScore: '',
  hasGmat: false,
  gmatDateOfExam: '',
  gmatTotalScore: '',
  gmatTotalRank: '',
  gmatVerbalScore: '',
  gmatVerbalRank: '',
  gmatQuantitativeScore: '',
  gmatQuantitativeRank: '',
  gmatAwaScore: '',
  gmatAwaRank: '',
  gmatDataInsightsScore: '',
  gmatDataInsightsRank: '',
  gmatIntegratedReasoningScore: '',
  gmatIntegratedReasoningRank: '',
  hasGre: false,
  greDateOfExam: '',
  greVerbalScore: '',
  greVerbalRank: '',
  greQuantitativeScore: '',
  greQuantitativeRank: '',
  greAnalyticalWritingScore: '',
  greAnalyticalWritingRank: '',
};

const SUPPORTED_FILES_MIME_TO_EXTENSION_MAP = new Map([
  ['image/jpeg', '.jpeg'],
  ['image/png', '.png'],
  ['application/pdf', '.pdf'],
]);

const transformFile = async (
  inputFile: File,
  documentType: string,
): Promise<{ file_name: string; content: string; type: string }> => {
  const base64 = (await getBase64(inputFile)) as string;
  const randomId = Math.floor(Math.random() * 999999) + 100000;
  const fileExtension = SUPPORTED_FILES_MIME_TO_EXTENSION_MAP.get(inputFile.type);
  if (!fileExtension) {
    throw new Error('Unsupported file type');
  }
  const fileName = `${documentType}-${randomId}${fileExtension}`;
  return { file_name: fileName, content: base64, type: documentType };
};

export const mapLoanDataToApplicationRequest = async (
  loanData: LoanData,
): Promise<CreateLoanApplicationV2Request> => {
  const { student } = loanData;

  const loanApplicationV2Request: Partial<CreateLoanApplicationV2Request> = {
    student_personal_information: {
      given_name: student.firstName,
      last_name: student.lastName,
      email_address: student.emailAddress,
      phone_number: student.contactPhoneNumber.replace('+', ''),
      whatsapp_number: student.contactWhatsAppNumber?.replace('+', ''),
      nationality: student.nationality,
    },
    loan_amount: 0,
    privacy_consent: false,
    terms_and_conditions_consent: false,
  };

  if (loanData.destinationDetails && loanData.destinationLeadDetails) {
    const { destinationDetails, destinationLeadDetails } = loanData;
    loanApplicationV2Request.destination_information = {
      destination_country: destinationLeadDetails.destination,
      field_of_study: destinationLeadDetails.fieldOfStudy,
      school_name: destinationLeadDetails.schoolName,
      program_name: destinationLeadDetails.programName,
      expected_arrival_date: new Intl.DateTimeFormat('en-CA').format(
        new Date(destinationDetails.expectedArrivalDate.trim()),
      ),
      expected_graduation_date: new Intl.DateTimeFormat('en-CA').format(
        new Date(destinationDetails.expectedGraduationDate.trim()),
      ),
    };
    loanApplicationV2Request.loan_amount = Number(destinationDetails.estimatedLoanAmount);
    loanApplicationV2Request.amount_currency = destinationDetails.loanAmountCurrency;
    if (loanData.recruitmentPartnerId) {
      loanApplicationV2Request.rpId = loanData.recruitmentPartnerId;
    }
  }

  if (loanData.educationSummary) {
    const { educationSummary } = loanData;
    let englishExamResult = {};
    let englishExamResultDoc;
    if (educationSummary.englishTestType == 'Duolingo') {
      englishExamResult = {
        overall: Number(educationSummary.englishOverallScore),
        exam_type: educationSummary.englishTestType,
        exam_date: new Intl.DateTimeFormat('en-CA').format(
          new Date(educationSummary.englishDateOfExam.trim()),
        ),
      };
      englishExamResultDoc = await transformFile(
        educationSummary.englishTestScoreDocument[0].file,
        'ENGLISH_EXAM_RESULT',
      );
    } else if (educationSummary.englishTestType) {
      englishExamResult = {
        listening: Number(educationSummary.englishListeningScore),
        writing: Number(educationSummary.englishWritingScore),
        reading: Number(educationSummary.englishReadingScore),
        speaking: Number(educationSummary.englishSpeakingScore),
        overall: Number(educationSummary.englishOverallScore),
        exam_type: educationSummary.englishTestType,
        exam_date: new Intl.DateTimeFormat('en-CA').format(
          new Date(educationSummary.englishDateOfExam.trim()),
        ),
      };
      englishExamResultDoc = await transformFile(
        educationSummary.englishTestScoreDocument[0].file,
        'ENGLISH_EXAM_RESULT',
      );
    }

    // do transformation as seen from rbcgic, convert into payload and attach here.

    loanApplicationV2Request.supporting_documents = englishExamResultDoc
      ? [englishExamResultDoc]
      : undefined;
    loanApplicationV2Request.education_summary = {
      english_exam_result: englishExamResult,
      competitive_test_results: {
        GRE: educationSummary.hasGre
          ? {
              verbal_score: Number(educationSummary.greVerbalScore),
              verbal_rank: Number(educationSummary.greVerbalRank),
              quantitative_score: Number(educationSummary.greQuantitativeScore),
              quantitative_rank: Number(educationSummary.greQuantitativeRank),
              analytical_writing_score: Number(educationSummary.greAnalyticalWritingScore),
              analytical_writing_rank: Number(educationSummary.greAnalyticalWritingRank),
              exam_date: new Intl.DateTimeFormat('en-CA').format(
                new Date(educationSummary.greDateOfExam.trim()),
              ),
            }
          : undefined,
        GMAT: educationSummary.hasGmat
          ? {
              verbal_score: Number(educationSummary.gmatVerbalScore),
              verbal_rank: Number(educationSummary.gmatVerbalRank),
              quantitative_score: Number(educationSummary.gmatQuantitativeScore),
              quantitative_rank: Number(educationSummary.gmatQuantitativeRank),
              analytical_writing_score: Number(educationSummary.gmatAwaScore),
              analytical_writing_rank: Number(educationSummary.gmatAwaRank),
              ...(educationSummary.gmatDataInsightsScore && {
                data_insights_score: Number(educationSummary.gmatDataInsightsScore),
              }),
              ...(educationSummary.gmatDataInsightsRank && {
                data_insights_rank: Number(educationSummary.gmatDataInsightsRank),
              }),
              ...(educationSummary.gmatIntegratedReasoningScore && {
                integrated_reasoning_score: Number(educationSummary.gmatIntegratedReasoningScore),
              }),
              ...(educationSummary.gmatIntegratedReasoningRank && {
                integrated_reasoning_rank: Number(educationSummary.gmatIntegratedReasoningRank),
              }),
              total_score: Number(educationSummary.gmatTotalScore),
              total_rank: Number(educationSummary.gmatTotalRank),
              exam_date: new Intl.DateTimeFormat('en-CA').format(
                new Date(educationSummary.gmatDateOfExam.trim()),
              ),
            }
          : undefined,
      },
    };
  }

  if (loanData.agreementAndConsent) {
    const { agreementAndConsent } = loanData;
    loanApplicationV2Request.privacy_consent = agreementAndConsent.consentToUsePrivateData;
    loanApplicationV2Request.terms_and_conditions_consent =
      agreementAndConsent.agreeTermsAndCondition;
  }

  if (loanData.source) {
    loanApplicationV2Request.source = loanData.source;
  }

  return loanApplicationV2Request as CreateLoanApplicationV2Request;
};

const EducationSummary = ({ loanData, setLoanData, goBack, goNext }: StepperState) => {
  const formId = useId();
  const { t } = useTranslation();
  const defaultData = loanData.educationSummary ?? DEFAULT_EDUCATIONAL_SUMMARY_VALUES;
  const toasts = useToasts();
  const utmSource = useGetUtmSource();
  const source = useGetSource();
  const { trackEvent } = useEventTracker();
  const { loanApplicationSubmission, isLoanApplicationSubmitting } =
    useLoanApplicationSubmissionV2();

  const submitCallback = useCallback(
    async (values: EducationSummaryForm) => {
      const { success: englishExamValid } = EnglishExamSchema(t).safeParse(values);
      const { success: greValid } = GREScoresSchema(t).safeParse(values);
      const { success: gmatValid } = GMATScoresSchema(t).safeParse(values);
      const formValid = [englishExamValid, greValid, gmatValid].every(value => value);

      if (!englishExamValid) {
        toasts.negative(new Error(t('loans.errors.englishExamInvalid')));
      }

      if (!greValid || !gmatValid) {
        toasts.negative(new Error(t('loans.errors.englishCompetitiveExamResultsInvalid')));
      }

      if (formValid) {
        setLoanData({
          ...loanData,
          educationSummary: values,
        });

        trackEvent({
          event: LoansFormEvents.CompletedEducationSummary,
          data: { rp_id: loanData.recruitmentPartnerId, utm_source: utmSource, source: source },
        });

        let loanApplicationRequest;
        try {
          loanApplicationRequest = await mapLoanDataToApplicationRequest({
            ...loanData,
            educationSummary: values,
          });
        } catch (e) {
          console.error('Failed to submit', e);

          const error = new Error(`Loans V2 Submission Validation failure. Reason ${e}`);
          trackEvent({
            event: LoansFormEvents.SubmissionValidationErrored,
            data: { rp_id: loanData.recruitmentPartnerId, utm_source: utmSource, source: source },
            error: error,
          });

          if (e instanceof ZodError) {
            toasts.negative(new Error(t('common.errors.invalidSubmission')));
          } else {
            toasts.negative(new Error(t('common.errors.unexpected')));
          }
          return;
        }

        setLoanData({
          ...loanData,
          educationSummary: values,
        });

        loanApplicationSubmission(
          loanApplicationRequest,
          result => {
            console.log('Loan application successfully submitted', result.data.id);

            trackEvent({
              event: LoansFormEvents.SubmissionCompleted,
              data: {
                rp_id: loanData.recruitmentPartnerId,
                utm_source: utmSource,
                source: source,
              },
            });
            goNext();
          },
          error => {
            console.error('Failed to submit loan application', error);
            let errorMessage = t('common.errors.submission');
            if (error instanceof LoansError) {
              const loansError = error as LoansError;
              errorMessage =
                loansError.errorBody.message instanceof Array
                  ? loansError.errorBody.message.join(',')
                  : loansError.errorBody.message;
              toasts.negative(new Error(errorMessage));
            } else {
              toasts.negative(new Error('Failed to submit loan application'));
            }
            const errorReason = new Error(errorMessage);
            trackEvent({
              event: LoansFormEvents.SubmissionErrored,
              data: {
                rp_id: loanData.recruitmentPartnerId,
                utm_source: utmSource,
                source: source,
              },
              error: errorReason,
            });
          },
        );
      }
    },
    [
      t,
      toasts,
      setLoanData,
      loanData,
      trackEvent,
      utmSource,
      source,
      goNext,
      loanApplicationSubmission,
    ],
  );

  return (
    <Form id={formId} defaultValues={defaultData} onSubmit={submitCallback}>
      <GridAlpha columns="repeat(27, 1fr)" rowGap={6} pb={6}>
        <GridAlpha.Item columnStart={2} columnEnd={27}>
          <Accordion onChange={function Na() {}}>
            <Accordion.Item
              id="1"
              title={
                <Flex gap={8} align="center">
                  {t('loans.educationSummary.englishScoresText')}
                  <EnglishTestScoreTag />
                </Flex>
              }
              autoOpen={true}
            >
              <EnglishTestScores />
            </Accordion.Item>
          </Accordion>
        </GridAlpha.Item>

        <GridAlpha.Item columnStart={2} columnEnd={27}>
          <Accordion onChange={function Na() {}}>
            <Accordion.Item
              id="2"
              title={
                <Flex gap={8} align="center">
                  {t('loans.educationSummary.competitiveScoresText')}
                  <GmatScoreTag />
                  <GreScoreTag />
                </Flex>
              }
              autoOpen={true}
            >
              <GMATScores />
              <GREScores />
            </Accordion.Item>
          </Accordion>
        </GridAlpha.Item>

        <GridAlpha.Item columnStart={2} columnEnd={13}>
          <Flex justify="start" pt={8}>
            <Button
              intent="secondary"
              size="lg"
              width="hug"
              leadIcon={ArrowLeftOutlineIcon}
              onClick={goBack}
              disabled={isLoanApplicationSubmitting}
            >
              {t('loans.back')}
            </Button>
          </Flex>
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={14} columnEnd={27}>
          <Flex justify="end" pt={8}>
            <Button
              intent="primary"
              size="lg"
              width="hug"
              trailIcon={ArrowRightOutlineIcon}
              type="submit"
              form={formId}
              disabled={isLoanApplicationSubmitting}
            >
              {t('loans.submit')}
            </Button>
          </Flex>
        </GridAlpha.Item>
      </GridAlpha>
    </Form>
  );
};

export default EducationSummary;

export { Field, useFieldValues, useSetFieldValues };
